import * as React from 'react'
import { useState } from 'react'
import { Button } from '@/ui/button'
import { Dropdown, type Option } from '@/ui/form/controls/Dropdown'
import { AdminIcon } from '@/modules/shared/components'
import { useConfirm } from '@/modules/shared/context/ConfirmContext'
import AddUserColor from '@/assets/icons_new/AddUserColor.svg'
import {
  ServiceAccountAccess,
  ServiceAccountAccessEntry,
  ServiceAccountAccessUser,
} from '@/modules/shared/query-client/useServiceAccountAdmin'
import { PersonKindTag } from './AccessPeople'
import { displayName, hasAccountWideAccess, inputClass, rolesIn, sameIds } from './shared'

type Save = (entries: ServiceAccountAccessEntry[]) => Promise<boolean>

const th =
  'tw-border-b tw-border-base-300 tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-uppercase tw-text-content-base tw-opacity-80'

export const AccessByCompany: React.FC<{
  access: ServiceAccountAccess
  roleOptions: Option[]
  busy: boolean
  onSave: Save
}> = ({ access, roleOptions, busy, onSave }) => {
  const [search, setSearch] = useState('')
  const [addingTo, setAddingTo] = useState<string | null>(null)
  const needle = search.trim().toLowerCase()
  const accountWideCount = access.users.filter(hasAccountWideAccess).length

  const companies = access.companies
    .map((company) => ({
      company,
      users: access.users.filter((u) => rolesIn(u, company.id).length > 0),
    }))
    .filter(
      ({ company, users }) =>
        !needle ||
        company.name.toLowerCase().includes(needle) ||
        users.some(
          (u) => u.login.toLowerCase().includes(needle) || displayName(u).toLowerCase().includes(needle),
        ),
    )

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-bg-content-primary">
      <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-4 tw-border-b tw-border-base-300 tw-px-4 tw-py-2">
        <AdminIcon name="search" />
        <input
          className="tw-w-full tw-border-0 tw-bg-transparent tw-py-2 tw-text-sm tw-outline-none"
          placeholder="Szukaj firmy lub osoby…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="tw-min-h-0 tw-flex-1 tw-overflow-auto">
        <table className="tw-w-full tw-border-collapse">
          <thead className="tw-sticky tw-top-0 tw-z-20 tw-bg-content-primary">
            <tr>
              <th className={th}>Osoba</th>
              <th className={th}>Rodzaj</th>
              <th className={`${th} tw-w-80`}>Role w firmie</th>
              <th className={`${th} tw-w-48`} />
            </tr>
          </thead>
          <tbody>
            {companies.map(({ company, users }) => (
              <React.Fragment key={company.id}>
                <tr className="tw-border-b tw-border-base-300 tw-bg-base-200">
                  <td colSpan={4} className="tw-px-4 tw-py-1.5">
                    <div className="tw-flex tw-items-center tw-gap-3">
                      <AdminIcon name="company" />
                      <span className="tw-text-sm tw-font-medium tw-text-content-base">{company.name}</span>
                      <span className="tw-text-xs tw-text-content-base tw-opacity-60">
                        {users.length === 0 ? 'nikt nie ma roli w tej firmie' : `osoby z rolą: ${users.length}`}
                        {accountWideCount > 0 && ` · użytkownicy konta: ${accountWideCount}`}
                      </span>
                      <button
                        type="button"
                        className="tw-ml-auto tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 tw-py-1 hover:tw-bg-base-300"
                        onClick={() => setAddingTo(addingTo === company.id ? null : company.id)}
                      >
                        <img src={AddUserColor} alt="" />
                        <span className="tw-text-sm tw-font-medium tw-text-primary">Dodaj osobę</span>
                      </button>
                    </div>
                  </td>
                </tr>
                {addingTo === company.id && (
                  <AddPersonRow
                    companyId={company.id}
                    users={access.users}
                    roleOptions={roleOptions}
                    busy={busy}
                    onCancel={() => setAddingTo(null)}
                    onSave={async (entries) => {
                      const ok = await onSave(entries)
                      if (ok) setAddingTo(null)
                      return ok
                    }}
                  />
                )}
                {users.map((u) => (
                  <UserRoleRow
                    key={u.userId}
                    user={u}
                    companyId={company.id}
                    companyName={company.name}
                    roleOptions={roleOptions}
                    busy={busy}
                    onSave={onSave}
                  />
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {companies.length === 0 && (
          <div className="tw-p-4 tw-text-center tw-text-xs tw-text-content-base">
            {access.companies.length === 0 ? 'Konto nie ma jeszcze żadnych firm.' : 'Brak wyników.'}
          </div>
        )}
      </div>
    </div>
  )
}

const UserRoleRow: React.FC<{
  user: ServiceAccountAccessUser
  companyId: string
  companyName: string
  roleOptions: Option[]
  busy: boolean
  onSave: Save
}> = ({ user, companyId, companyName, roleOptions, busy, onSave }) => {
  const confirm = useConfirm()
  const current = rolesIn(user, companyId)
  const [draft, setDraft] = useState<number[]>(current)
  const dirty = !sameIds(draft, current)
  const name = displayName(user)

  React.useEffect(() => setDraft(current), [current.join(',')]) // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async () => {
    const ok = await confirm({
      title: 'Odebrać dostęp do firmy?',
      message: `${user.login} straci wszystkie role w firmie „${companyName}”.`,
      confirmText: 'Odbierz',
      cancelText: 'Anuluj',
    })
    if (ok) await onSave([{ login: user.login, companyId, roleIds: [] }])
  }

  return (
    <tr className="tw-border-b tw-border-base-300 hover:tw-bg-primary/10">
      <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-content-base">
        <div>{name || user.login}</div>
        {name && <div className="tw-text-xs tw-opacity-60">{user.login}</div>}
      </td>
      <td className="tw-px-4 tw-py-3">
        <PersonKindTag user={user} />
      </td>
      <td className="tw-px-4 tw-py-2">
        <Dropdown
          compact
          portalMenu
          multiple
          showSelectedNames
          options={roleOptions}
          value={draft.map(String)}
          onChange={(values: string[]) => setDraft(values.map(Number))}
          placeholder="Wybierz rolę"
          disabled={busy}
        />
      </td>
      <td className="tw-px-4 tw-py-2">
        <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
          {dirty && (
            <>
              <Button
                size="xs"
                disabled={busy || draft.length === 0}
                onClick={() => onSave([{ login: user.login, companyId, roleIds: draft }])}
              >
                Zapisz
              </Button>
              <Button size="xs" variant="tertiary" onClick={() => setDraft(current)}>
                Cofnij
              </Button>
            </>
          )}
          <button
            type="button"
            title="Odbierz dostęp do tej firmy"
            className="tw-rounded tw-p-1.5 hover:tw-bg-base-300 disabled:tw-opacity-50"
            disabled={busy}
            onClick={remove}
          >
            <AdminIcon name="delete" className="tw-text-error" />
          </button>
        </div>
      </td>
    </tr>
  )
}

const AddPersonRow: React.FC<{
  companyId: string
  users: ServiceAccountAccessUser[]
  roleOptions: Option[]
  busy: boolean
  onCancel: () => void
  onSave: Save
}> = ({ companyId, users, roleOptions, busy, onCancel, onSave }) => {
  const [login, setLogin] = useState('')
  const [firstName, setFirstName] = useState('')
  const [surname, setSurname] = useState('')
  const [roleIds, setRoleIds] = useState<number[]>([])

  return (
    <tr className="tw-border-b tw-border-base-300 tw-bg-primary/10">
      <td className="tw-px-4 tw-py-2" colSpan={2}>
        <div className="tw-flex tw-gap-2">
          <input
            className={inputClass}
            type="email"
            placeholder="Adres e-mail"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            autoFocus
          />
          <input className={inputClass} placeholder="Imię" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          <input className={inputClass} placeholder="Nazwisko" value={surname} onChange={(e) => setSurname(e.target.value)} />
        </div>
        <div className="tw-mt-1 tw-text-xs tw-text-content-base tw-opacity-60">
          Dla osoby spoza konta podaj imię i nazwisko. Bez konta na platformie dostanie e-mail z linkiem do ustawienia hasła.
        </div>
      </td>
      <td className="tw-px-4 tw-py-2 tw-align-top">
        <Dropdown
          compact
          portalMenu
          multiple
          showSelectedNames
          options={roleOptions}
          value={roleIds.map(String)}
          onChange={(values: string[]) => setRoleIds(values.map(Number))}
          placeholder="Wybierz rolę"
        />
      </td>
      <td className="tw-px-4 tw-py-2 tw-align-top">
        <div className="tw-flex tw-justify-end tw-gap-2">
          <Button
            size="sm"
            disabled={
              busy ||
              !login.includes('@') ||
              roleIds.length === 0 ||
              // Osoba spoza konta: serwer wymaga imienia i nazwiska.
              (!users.some((u) => u.login.toLowerCase() === login.trim().toLowerCase()) &&
                (!firstName.trim() || !surname.trim()))
            }
            onClick={() => {
              // Zapis podmienia cały zestaw ról w firmie - osobie, która już tu jest, role dopisujemy.
              const already = users.find((u) => u.login.toLowerCase() === login.trim().toLowerCase())
              const merged = Array.from(new Set([...(already ? rolesIn(already, companyId) : []), ...roleIds]))
              onSave([
                { login: login.trim(), firstName: firstName.trim(), surname: surname.trim(), companyId, roleIds: merged },
              ])
            }}
          >
            Dodaj
          </Button>
          <Button size="sm" variant="tertiary" onClick={onCancel}>
            Anuluj
          </Button>
        </div>
      </td>
    </tr>
  )
}
